import {
    Form,
    StyleData,
    InputControl,
    InputLabel,
    IconButton,
    RemovableRow,
    TextInput,
} from "@venuepos/react-common";
import { IProductInput } from "lib";
import { useTranslation } from "locales";
import React, { useMemo } from "react";

export const BarcodeSection = React.memo(function BarcodeSection({
    value,
    error,
    styles,
    t,
    productId,
    isExternal,
    onAddBarcode,
    onBarcodeChange,
    onRemoveBarcode,
}: {
    value: IProductInput["barcodes"];
    error: Form<IProductInput>[0]["errors"]["barcodes"];
    styles: StyleData;
    t: ReturnType<typeof useTranslation>[0];
    productId?: string;
    isExternal: boolean;
    onAddBarcode: () => void;
    onBarcodeChange: (text: string, index: number) => void;
    onRemoveBarcode: (_item: any, index: number) => void;
}) {
    const barcodeItems = useMemo(
        () =>
            value?.map((item, index) => {
                const key = `barcode_row:${productId || "no_id"}_${index}`;

                return (
                    <BarcodeRow
                        key={key}
                        item={item}
                        index={index}
                        isExternal={isExternal}
                        onBarcodeChange={onBarcodeChange}
                        onRemoveBarcode={onRemoveBarcode}
                        styles={styles}
                        t={t}
                    />
                );
            }),
        [
            isExternal,
            onBarcodeChange,
            onRemoveBarcode,
            productId,
            styles,
            t,
            value,
        ]
    );

    return (
        <InputControl
            error={error}
            description={
                isExternal
                    ? t(
                          "backoffice.common.readonly_value",
                          "This value is controlled by an external system via the API and can only be changed from here."
                      )
                    : t(
                          "backoffice.product_form.barcodes_description",
                          "Enter one or more barcodes for the product by pressing +"
                      )
            }
        >
            <InputLabel>
                {t("backoffice.product_form.barcode", "Barcode")}
                {!isExternal && (
                    <IconButton
                        name="plus"
                        size="small"
                        onPress={onAddBarcode}
                        style={styles.addBarcode}
                    />
                )}
            </InputLabel>
            {barcodeItems}
        </InputControl>
    );
});

export const BarcodeRow = React.memo(function BarcodeRow({
    item,
    index,
    isExternal,
    onBarcodeChange,
    onRemoveBarcode,
    styles,
    t,
}: {
    item: NonNullable<IProductInput["barcodes"]>[number];
    index: number;
    isExternal: boolean;
    onBarcodeChange: (text: string, index: number) => void;
    onRemoveBarcode: (_item: any, index: number) => void;
    styles: StyleData;
    t: ReturnType<typeof useTranslation>[0];
}) {
    return (
        <RemovableRow
            onRemove={() => {
                onRemoveBarcode(item, index);
            }}
            disabled={isExternal}
        >
            <TextInput
                style={styles.textInput}
                placeholder={t(
                    "backoffice.product_form.enter_barcode",
                    "Enter barcode"
                )}
                value={item.barcode || ""}
                onChangeText={newValue => onBarcodeChange(newValue, index)}
                disabled={isExternal}
                testID="barcode"
            />
        </RemovableRow>
    );
});
